import React from "react";
import { Printer, Square } from "lucide-react";

function Group({ n, title, children }) {
  return (
    <section className="mt-8 break-inside-avoid" data-testid={`checklist-group-${n}`}>
      <h2 className="font-display text-xl tracking-tight border-b border-black/15 pb-2">
        <span className="text-neutral-400 mr-2">{n}.</span>{title}
      </h2>
      <ul className="mt-3 space-y-2.5">{children}</ul>
    </section>
  );
}

function Item({ children, note }) {
  return (
    <li className="flex gap-3 text-sm leading-relaxed">
      <Square size={15} strokeWidth={1.4} className="mt-0.5 shrink-0 text-neutral-400" />
      <span>
        {children}
        {note && <span className="block text-xs text-neutral-500 mt-0.5">{note}</span>}
      </span>
    </li>
  );
}

export default function LaunchChecklist() {
  return (
    <div data-testid="launch-checklist" className="print-page max-w-[820px] mx-auto px-6 py-10">
      <div className="flex items-start justify-between gap-4 border-b border-black/15 pb-5">
        <div>
          <div className="label-eyebrow">Your Classic Style</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tighter mt-1">Go-Live Checklist.</h1>
          <p className="mt-2 text-sm text-neutral-500 max-w-xl">
            Everything to sort before you open to real buyers and sellers. Tick each box as you go.
          </p>
        </div>
        <button onClick={() => window.print()} data-testid="print-btn"
          className="btn-primary shrink-0 no-print">
          <Printer size={14} /> Print
        </button>
      </div>

      <Group n={1} title="Business & legal">
        <Item note="This can be your own name if you're a sole trader, or a registered company name.">
          Decide the <strong>legal name</strong> that will run the marketplace.
        </Item>
        <Item note="This sets the governing law / courts in the Terms (Section 22) and the 'who we are' in the Privacy Policy.">
          Pick your <strong>country and state/region</strong> of operation.
        </Item>
        <Item note="Send me these and I'll drop them into the Terms & Privacy pages (they're placeholders right now).">
          Create a <strong>support email</strong> and a <strong>privacy email</strong> (e.g. support@ and privacy@ your domain).
        </Item>
        <Item note="The Terms and Privacy pages are solid templates but not legal advice.">
          Have a <strong>lawyer review the Terms &amp; Privacy Policy</strong>, especially the prohibited/dangerous-goods rules and consumer-refund rights for your region.
        </Item>
        <Item>Confirm whether you must <strong>collect sales tax / VAT</strong> and register if required.</Item>
      </Group>

      <Group n={2} title="Payments & money (currently in test/demo mode)">
        <Item note="Right now checkout, payouts and subscriptions run on a Stripe TEST sandbox.">
          <strong>Claim / verify your Stripe account</strong> so real card payments work.
        </Item>
        <Item note="Buyers can pay today in test mode; going live needs the verified account.">
          Confirm <strong>real order checkout</strong> end-to-end once live.
        </Item>
        <Item note="Today the Wallet 'Withdraw' is simulated — no real bank transfer happens.">
          Decide on <strong>seller payouts</strong>: set up Stripe Connect so sellers can link a bank and get paid (each seller does identity/bank verification).
        </Item>
        <Item note="Today Pro/Store plans just unlock tools; no card is charged monthly.">
          Turn on <strong>real recurring billing</strong> for Pro/Store seller plans (optional).
        </Item>
        <Item>Confirm your <strong>fee numbers</strong> — buyer protection %, seller commission, authentication fees, shipping rates — match your final policy.</Item>
      </Group>

      <Group n={3} title="Shipping & fulfilment">
        <Item note="Current rates (locker/home/express/pickup) and pickup points are sensible defaults, not carrier contracts.">
          Set up <strong>real carrier accounts / rates</strong> and, if using pickup points, a live locker network.
        </Item>
        <Item>Write your <strong>shipping & delivery timeframes</strong> and returns stance for buyers.</Item>
        <Item>Confirm the <strong>buyer-protection guarantee window</strong> (currently 3 days from delivery) is what you want.</Item>
      </Group>

      <Group n={4} title="Trust, safety & authenticity">
        <Item note="AI screening + the hazardous-keyword hold are aids, not a guarantee.">
          Decide who <strong>reviews held / flagged listings</strong> (authenticity and hazardous-goods holds) and how sellers appeal.
        </Item>
        <Item>Set your <strong>counterfeit / strike policy</strong> thresholds and a takedown/notice process for rights holders.</Item>
        <Item>Confirm the <strong>designer authentication threshold and fees</strong> for high-value items.</Item>
        <Item>Prepare <strong>customer-support coverage</strong> for claims, disputes and refunds.</Item>
      </Group>

      <Group n={5} title="Content & data">
        <Item note="Replace demo/test listings and the sample pickup points and proof images.">
          Remove <strong>test data and demo accounts</strong>; seed real starter inventory if you like.
        </Item>
        <Item>Review the <strong>homepage imagery and copy</strong> so it reflects your real brand.</Item>
        <Item>Add a <strong>cookie/consent banner</strong> if your region requires one, and list your analytics tools in the Privacy Policy.</Item>
      </Group>

      <Group n={6} title="Launch">
        <Item>Set up your <strong>custom domain</strong> and publish/deploy the app.</Item>
        <Item>Do a <strong>full test run</strong>: register → list → buy → ship → deliver → review → (test) payout.</Item>
        <Item>Line up a few <strong>launch sellers</strong> (and early makers via the Artisan Ateliers waitlist).</Item>
        <Item>Plan your <strong>launch announcement</strong> and how buyers will find you.</Item>
      </Group>

      <p className="mt-10 text-xs text-neutral-400 border-t border-black/10 pt-4">
        Generated for Your Classic Style · Items marked "test/demo mode" are functional in preview but need real
        setup before taking live money. This checklist is guidance, not legal or financial advice.
      </p>
    </div>
  );
}
